import { useEffect, useMemo, useRef } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import type { AdminOutputs } from 'shared/api'
import { useAdminTrpc } from 'shared/api'
import { Badge } from 'shared/components/ui/badge'
import { CopyChip } from 'shared/components/CopyChip'
import { formatUzsAmount } from 'shared/lib/format'
import { formatDateTime } from 'shared/lib/utils'
import { useTranslate, type TranslationPaths } from 'shared/lib/i18n'
import { RefundButton } from './RefundButton'

type Purchase = AdminOutputs['purchases']['ListHistory']['items'][number]
type PurchaseStatus = Purchase['status']

/**
 * One label per derived purchase status. `satisfies` over the full union keeps
 * this exhaustive: a contract status change breaks the typecheck here instead
 * of rendering an undefined key. Shares the `status.order` catalog with the
 * buyer-facing My Orders view.
 */
const STATUS_KEYS = {
	active: 'status.order.active',
	partially_refunded: 'status.order.partiallyRefunded',
	refunded: 'status.order.refunded',
	refund_pending: 'status.order.refundPending',
} as const satisfies Record<PurchaseStatus, TranslationPaths>

const STATUS_VARIANT = {
	active: 'default',
	partially_refunded: 'secondary',
	refunded: 'secondary',
	refund_pending: 'destructive',
} as const satisfies Record<PurchaseStatus, 'default' | 'secondary' | 'destructive'>

/** A checkout still has something to refund unless every line is exhausted. */
function isRefundable(purchase: Purchase): boolean {
	return purchase.lines.some((line) => line.quantity - line.refundedQuantity > 0)
}

export function PaymentsHistory({ buyerUserId }: { buyerUserId?: string }) {
	const trpc = useAdminTrpc()
	const { t, prefixT } = useTranslate('pages.adminPurchases')
	const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
		...trpc.purchases.ListHistory.infiniteQueryOptions(
			{ buyerUserId },
			{ getNextPageParam: (last) => last.nextCursor, initialCursor: 0 },
		),
	})
	const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data])

	// Infinite scroll: fetch the next page when the sentinel nears the viewport.
	const sentinelRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const el = sentinelRef.current
		if (!el) return
		const io = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage)
					void fetchNextPage()
			},
			{ rootMargin: '600px' },
		)
		io.observe(el)
		return () => io.disconnect()
	}, [hasNextPage, isFetchingNextPage, fetchNextPage])

	return (
		<div className='flex flex-col gap-2'>
			{items.length === 0 && !isFetchingNextPage && (
				<div className='text-muted-foreground'>{prefixT('noEntries')}</div>
			)}
			{items.map((p) => (
				<div key={p._id} className='rounded-lg border p-3 text-sm'>
					<div className='flex items-center justify-between gap-2'>
						<span className='font-medium'>
							{p.orderNumber != null
								? prefixT('orderTitle', { number: String(p.orderNumber), title: p.lotTitle })
								: p.lotTitle}
						</span>
						<Badge variant={STATUS_VARIANT[p.status]}>{t(STATUS_KEYS[p.status])}</Badge>
					</div>
					<div className='mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-muted-foreground'>
						{p.lines.map((line) => (
							<span key={line.inventoryId}>
								{prefixT('lineLabel', { name: line.name, quantity: String(line.quantity) })}
							</span>
						))}
					</div>
					<div className='mt-1 font-medium'>
						{t('common.amountUzs', { amount: formatUzsAmount(p.totalUzs) })}
						{p.refundedUzs > 0 && (
							<span className='ml-2 text-muted-foreground'>
								{prefixT('refundedTotal', {
									amount: t('common.amountUzs', { amount: formatUzsAmount(p.refundedUzs) }),
								})}
							</span>
						)}
					</div>
					<div className='mt-1 flex flex-wrap items-center gap-2 text-muted-foreground'>
						<span>{p.buyerName}</span>
						{p.buyerTezId && <CopyChip value={p.buyerTezId} />}
						<span>{formatDateTime(p.createdAt)}</span>
					</div>
					{isRefundable(p) && (
						<div className='mt-2'>
							<RefundButton purchase={p} />
						</div>
					)}
				</div>
			))}
			{isFetchingNextPage && <div className='text-muted-foreground'>{t('common.loading')}</div>}
			<div ref={sentinelRef} />
		</div>
	)
}
